// Resource Calculator V2 — engine type contract.
// Data and calculations use exact levels L1–L8; users only ever see the four
// level groups in `stages/level-groups.ts` (Junior, Mid-Level, Manager, Executive).

export type LevelId = 'L1' | 'L2' | 'L3' | 'L4' | 'L5' | 'L6' | 'L7' | 'L8';
export const LEVEL_IDS: readonly LevelId[] = ['L1', 'L2', 'L3', 'L4', 'L5', 'L6', 'L7', 'L8'] as const;

export type ChannelCategory = 'traditional' | 'addressable';
/** The engine runs one pipeline: direct tables, keyed off the model's tables. */
export type EngineMode = 'direct-tables';
export type DimensionAppliesTo = 'structural' | 'automation' | 'enterprise_platform' | 'omni' | 'meta';
export type JuniorDriver = 'totalBudget' | 'channelBudget';

// ---------------------------------------------------------------------------
// Model
// ---------------------------------------------------------------------------

export interface Channel {
  id: string;
  label: string;
  category: ChannelCategory;
  active?: boolean;
  adopsApplicable?: boolean;
}

export interface ChannelMixPreset {
  id: string;
  label: string;
  description?: string;
  allocation: Record<string, number> | null;
}

export interface DimensionOption {
  value: string;
  label: string;
  /**
   * Blanket multiplier applied to every cell. Ignored when `targetedFactors`
   * is non-empty — a targeted option carries all of its weight there.
   */
  factor?: number;
  targetedFactors?: TargetedFactor[];
  note?: string;
}

export interface TargetedFactor {
  levels: LevelId[] | null;
  disciplines: string[] | null;
  factor: number;
  note?: string;
}

export type DimensionKind = 'select' | 'multiselect';

export interface Dimension {
  id: string;
  label: string;
  /** `select` = one option; `multiselect` = zero-or-more (factors multiply). */
  kind: DimensionKind;
  appliesTo: DimensionAppliesTo;
  description?: string;
  options: DimensionOption[];
}

export interface ActivationConditions {
  rampWindow?: {
    startBudget: number;
    fullBudget: number;
    shape?: 'sigmoid' | 'linear';
  };
  geoBypass?: string[];
}

/** Which channels a discipline's staff plan rows split across. */
export interface JuniorRatio {
  driver: JuniorDriver;
  channelCategoryFilter?: ChannelCategory;
  channelIdFilter?: string[];
}

export interface Discipline {
  id: string;
  label: string;
  active?: boolean;
  juniorRatio: JuniorRatio;
  activationConditions?: ActivationConditions;
}

export type Shore = 'onshore' | 'nearshore' | 'offshore';

export interface SpendPerFteRow {
  disciplineId: string;
  /** Either a channel id, or 'all' for discipline-wide. */
  channelId: string;
  /** $ per FTE (annual). null = not staffed off this basis. */
  ratio: number | null;
  basis: 'totalBudget' | 'channelBudget' | 'custom';
  /** When basis = custom, identifies the budget pool key (e.g. "programmatic+digital_direct"). */
  customBasisKey?: string;
  note?: string;
}

export interface LevelWeightRow {
  disciplineId: string;
  channelId: string;
  /** Weights summing to ~1 across levels. Missing levels treated as 0. */
  weights: Partial<Record<LevelId, number>>;
}

export interface CostPerRoleRow {
  shore: Shore;
  /** Specific OGS hub for nearshore/offshore rows. Onshore rows ignore this. */
  hubId?: string;
  disciplineId: string;
  channelId: string;
  levelId: LevelId;
  /** Fully-loaded annual cost per FTE (USD). */
  annualCost: number;
}

/**
 * Gates whether a (discipline × channel) row is offered as an OGS option in
 * the scenario intake. Missing entry = eligible (default). Set `enabled:
 * false` to hide a discipline/channel's OGS slider from the intake (e.g.
 * Business Leadership should never be offshored).
 */
export interface OgsEligibilityRow {
  disciplineId: string;
  channelId: string;
  enabled: boolean;
}

/** Per-hub-location share of each (discipline × channel × level) cell. */
export interface HubPctRow {
  /** References `Model.ogsHubs[].id`. */
  hubId: string;
  disciplineId: string;
  channelId: string;
  levelId: LevelId;
  /** 0..1 — share of this cell that lands at this specific hub. */
  pct: number;
}

export interface TimeConcentrationRow {
  /** Sub-discipline key from maturity-taxonomy (e.g. "Strategy_All", "Investment_Programmatic"). */
  subDisciplineKey: string;
  /** Sub-phase id from maturity-taxonomy. */
  subPhaseId: string;
  levelId: LevelId;
  /** 0..1 share of that level's time spent on this sub-phase. */
  share: number;
}

export interface BudgetBucket {
  id: string;
  label: string;
  minUsd: number;
  /** null = open-ended top bucket. */
  maxUsd: number | null;
}

export interface CampaignBucket {
  id: string;
  label: string;
  min: number;
  max: number | null;
}

export interface OgsHub {
  id: string;
  label: string;
  shore: 'nearshore' | 'offshore';
  /**
   * Subset of `Model.ogsFunctions` this hub is allowed to staff. When empty or
   * omitted the hub is treated as supporting every function (back-compat for
   * pre-coverage data). Drives the function chips shown in the intake's OGS
   * cards once a hub has been picked.
   */
  functionIds?: string[];
}

export interface OgsFunction {
  id: string;
  label: string;
}

/** ISO-4217 currency code, e.g. 'USD', 'GBP'. */
export type CurrencyCode = string;

/**
 * A currency the model knows about, quoted against USD. USD is the hub for all
 * conversions, so each currency needs exactly one number and there is no
 * arbitrary cross-rate matrix to keep consistent.
 */
export interface CurrencyRate {
  code: CurrencyCode;
  /** Units of `code` per 1 USD. USD itself is implicitly 1. */
  perUsd: number;
  /** Free-text provenance, e.g. 'ECB 2026-08-01'. */
  asOf?: string;
}

export interface CurrencyConfig {
  /**
   * Currency every monetary model input is expressed in (cost per role, spend
   * per FTE, budget buckets, scenario budgets). When this is not 'USD',
   * `rates` must carry a `perUsd` entry for it — that rate is the only path
   * back to USD for display and for portfolio reporting.
   */
  base: CurrencyCode;
  rates: CurrencyRate[];
  /** Currency outputs open in. Must be one of the model's display options. */
  displayDefault?: CurrencyCode;
}

export interface Model {
  id: string;
  name: string;
  parentId?: string | null;
  version?: number;
  engineMode?: EngineMode;
  createdAt?: string;
  updatedAt?: string;
  notes?: string;
  channels: Channel[];
  channelMixPresets: ChannelMixPreset[];
  dimensions: Dimension[];
  disciplines: Discipline[];

  // ---- Direct-table backbone -----------------------------------------------
  spendPerFte?: SpendPerFteRow[];
  levelWeights?: LevelWeightRow[];
  costPerRole?: CostPerRoleRow[];
  /** Per-hub-location share of each cell that lands away from onshore. */
  hubPct?: HubPctRow[];
  timeConcentration?: TimeConcentrationRow[];
  budgetBuckets?: BudgetBucket[];
  campaignBuckets?: CampaignBucket[];
  ogsHubs?: OgsHub[];
  ogsFunctions?: OgsFunction[];
  /** Per-(discipline × channel) gate for whether the intake offers an OGS
   *  slider. Missing rows are treated as eligible. */
  ogsEligibility?: OgsEligibilityRow[];
  /** Named OGS slider presets offered in the scenario intake. */
  ogsPresets?: OgsPreset[];
  /** Named Leveling slider presets offered in the scenario intake. */
  levelMixPresets?: LevelMixPreset[];
  /**
   * Strength of the Leveling tab's seniority tilt at the slider extremes.
   * Higher = a more aggressive shift toward junior/executive at ±100%;
   * 0 disables the effect. When omitted the engine default is used
   * (see `SENIORITY_TILT_STRENGTH`).
   */
  seniorityTiltStrength?: number;
  /**
   * Currency the model's monetary tables are expressed in, plus the USD-quoted
   * rates that determine which currencies outputs may be displayed in. When
   * omitted the model is treated as USD-only.
   */
  currency?: CurrencyConfig;
  /**
   * Staff Plan upload/parser setup. Not consumed by the scenario calculation
   * engine — sent to the AI parser when a workbook is uploaded.
   */
  staffPlanParser?: StaffPlanParserConfig;
}

export interface StaffPlanParserConfig {
  /**
   * The full parser skill (markdown), used as the AI system prompt. Includes
   * the model-specific title / channel / location mappings. When unset, the
   * engine's DEFAULT_STAFF_PLAN_PARSER_SKILL applies.
   */
  skill?: string;
}

export type StaffPlanRoleCategory = 'standard' | 'other';

export type StaffPlanImportAiConfidence = 'high' | 'medium' | 'low';

/**
 * One person (or role line) from an uploaded staff plan, as produced by the
 * parser and edited in the import wizard's Review step. Never stored on the
 * server; Summary aggregates rows into StaffPlan cells / other roles.
 */
export interface StaffPlanImportRow {
  id: string;
  include: boolean;

  // ---- Parsed / detected (from source roster) ------------------------------
  name?: string;
  email?: string;
  title: string;
  sourceFunction?: string;
  sourceChannel?: string;
  sourceFte: number;
  /** Detected OGS/hubbed signal from source. */
  sourceOgsHubbed?: boolean;
  sourceHubLocation?: string;
  sourceSheet?: string;

  // ---- Mapped (user-editable classification) --------------------------------
  /** Exact matrix level (L1–L8); views show its level group. */
  levelId?: LevelId;
  roleCategory?: StaffPlanRoleCategory;
  /** Model discipline; Standard rows only. */
  disciplineId?: string;
  /** Role label (e.g. Influencer, or free text); Other rows only. */
  otherRole?: string;
  /** Standard rows only. */
  channelId?: string;
  /** Mapped FTE allocation (defaults from sourceFte). */
  mappedFte?: number;
  /** Hub Yes/No — Yes sends mapped FTE to hub shore on aggregate. */
  hub?: boolean;
  /** References `Model.ogsHubs[].id` when classified against the model. */
  hubId?: string;
  /** Display label (from model hub or free-text legacy). */
  hubLocation?: string;
  notes?: string;

  /** True when classification is incomplete for matrix aggregation. */
  unmapped?: boolean;

  /**
   * Parser / AI confidence for the mapped classification.
   * Low confidence should always include an `aiComment` explaining why.
   */
  aiConfidence?: StaffPlanImportAiConfidence;
  /** Rationale or low-confidence commentary from the parser / AI pass. */
  aiComment?: string;
}

/** A specific automation that reduces time spent on a scoped slice of work. */
export interface AutomationImpactRow {
  id: string;
  label: string;
  description?: string;
  reductionPct: number;
  disciplineChannelKeys: string[];
  levelIds: LevelId[];
  subPhaseIds: string[];
  active?: boolean;
}

// ---------------------------------------------------------------------------
// Client profile + scenario
// ---------------------------------------------------------------------------

export interface GeoModelPair {
  workingLocation: string;
  activationMarkets: string[];
}

export interface GeoModel {
  type: 'single' | 'hub';
  pairs: GeoModelPair[];
}

/**
 * Per-discipline (optionally per-channel) OGS configuration (scenario-driven).
 * A single combined "OGS" bucket: the chosen hub's `shore` decides whether the
 * FTE lands in the nearshore or offshore cost leg internally. The `pct` is a
 * flat share applied to each selected level; levels not listed get 0% OGS.
 *
 * `channelId` scopes the entry to one channel. Multi-channel disciplines (e.g.
 * Investment) carry one entry per channel so each can be tuned independently.
 * Single-channel disciplines use `'all'` (or omit it) to cover every channel.
 */
export interface OgsDisciplineConfig {
  disciplineId: string;
  /** Channel scope. `'all'` / undefined applies to every channel of the discipline. */
  channelId?: string;
  enabled: boolean;
  /** References `Model.ogsHubs[].id`. The hub's shore drives the cost leg. */
  hubId?: string;
  /** 0..1 — flat share of each selected level's FTE that lands at the hub. */
  pct: number;
  /** Which levels (roles) the `pct` applies to. */
  levels: LevelId[];
}

export interface OgsConfig {
  /** Per-discipline OGS config — the source of truth for the shore split. */
  disciplines?: OgsDisciplineConfig[];
}

/**
 * A named, model-defined OGS preset — a saved set of per-discipline/channel
 * OGS slider values. Selecting it in the intake copies these onto the
 * scenario as a starting point; the user can still fine-tune afterwards.
 */
export interface OgsPreset {
  id: string;
  name: string;
  disciplines: OgsDisciplineConfig[];
}

export type AnalyticsAddon = 'data_viz_reporting' | 'marketing_science' | 'audience';

/**
 * Per-discipline (optionally per-channel) leveling override (scenario-driven).
 *
 * A single seniority `shift` in [-1, +1] marginally tilts the level
 * composition: 0 = the model's default mix, negative skews toward Junior,
 * positive toward Executive. The engine applies it as a bounded multiplier on
 * the base `levelWeights` and renormalizes, so the discipline/channel total
 * FTE is preserved and levels with no base weight stay empty.
 *
 * `channelId` scopes the entry to one channel (multi-channel disciplines like
 * Investment carry one entry per channel); `'all'`/undefined covers every
 * channel of the discipline.
 */
export interface LevelMixOverride {
  disciplineId: string;
  /** Channel scope. `'all'` / undefined applies to every channel. */
  channelId?: string;
  /** -1 (all Junior) .. 0 (model default) .. +1 (all Executive). */
  shift: number;
}

/**
 * A named, model-defined Leveling preset — a saved set of per-discipline/
 * channel seniority shifts. Selecting it in the intake copies these onto the
 * scenario as a starting point; the user can still fine-tune afterwards.
 */
export interface LevelMixPreset {
  id: string;
  name: string;
  overrides: LevelMixOverride[];
}

export interface ClientProfile {
  totalBudget: number;
  channelMixPresetId?: string;
  channelAllocation: Record<string, number>;
  /** Dimension answers: single-select string or multiselect string[]. */
  selections: Record<string, string | string[]>;
  /** Active channel toggles (chooses which channels are in-market). */
  activeChannels?: Record<string, boolean>;
  /** Per-channel AdOps support flag. */
  adopsSupport?: Record<string, boolean>;
  /** Keys are `${disciplineId}:${levelId}`. */
  currentStaffByLevel: Record<string, number>;
  /** Optional: allow simple totals-only entry. Keys are discipline ids. */
  currentStaffByDiscipline?: Record<string, number>;

  // ---- V3 structural intake additions --------------------------------------
  /** Free-form LOB / sub-brand names. */
  lobs?: string[];
  /** ISO country codes the scenario covers (e.g. ["US","CA","MX"]). */
  scenarioMarkets?: string[];
  geoModel?: GeoModel;
  ogs?: OgsConfig;
  /** Selected OGS preset id (from `model.ogsPresets`), or 'custom'/undefined
   *  once the user has hand-tuned the sliders. UI display only. */
  ogsPresetId?: string;
  /** Per-discipline (optionally per-channel) seniority-tilt overrides. */
  levelMix?: LevelMixOverride[];
  /** Selected Leveling preset id (from `model.levelMixPresets`), or
   *  'custom'/undefined once the user has hand-tuned the sliders. */
  levelMixPresetId?: string;
  /** Active Analytics sub-discipline add-ons. */
  analyticsAddons?: AnalyticsAddon[];
  /** Pointer to the saved Current Staff Plan that produced `currentStaffByLevel`. */
  currentStaffPlanId?: string;
  /** Pointer to the saved Spend Set that populated the channel spend fields. */
  currentSpendSetId?: string;
  /** Pointer to the saved Automation Solution Set applied to this scenario. */
  currentAutomationSolutionId?: string;
  /** The applied set's `updatedAt` at the time it was applied, so the UI can
   *  tell when the set has changed since. */
  currentAutomationSolutionUpdatedAt?: string;
  /** Automations copied from the applied set. Calculations read this copy,
   *  so a scenario keeps its numbers if the set later changes or is deleted. */
  automations?: AutomationImpactRow[];
  /** Per-automation deployment timing for the Automation Impact output view. */
  automationDeployments?: Record<string, AutomationDeploymentSetting>;
  /** Manual FTE edits layered on top of the modeled grid, producing the
   *  "Modeled-Adjusted" plan. Keyed by
   *  `${disciplineId}::${channelId}::${levelId}`. Sparse: only edited cells
   *  appear, so an untouched scenario carries nothing. */
  staffPlanOverrides?: Record<string, StaffPlanCellOverride>;
  /** Which staffing basis the Automation Impact view applies its reduction
   *  percentages to. Defaults to 'modeled'. */
  automationBasis?: AutomationBasis;
}

/** Per-automation deployment timing held on the scenario profile. */
export interface AutomationDeploymentSetting {
  startMonth?: number;
  rampMonths?: number;
  enabled?: boolean;
}

/**
 * One hand-edited cell of the modeled staff plan.
 *
 * `onFte` / `hubFte` are pinned absolute values: they are what the user typed
 * and they survive the modeled numbers moving underneath them. The `baseline*`
 * fields record what the model said at the moment of the edit, which is how
 * drift is detected — when a baseline no longer matches the live modeled cell,
 * the model has moved and the UI can offer to re-baseline while keeping the
 * manual figures. Storing the baseline per cell is more precise than
 * fingerprinting the whole input set: it names exactly which cells drifted.
 */
export interface StaffPlanCellOverride {
  onFte: number;
  hubFte: number;
  baselineOnFte: number;
  baselineHubFte: number;
  /** ISO timestamp of the edit. */
  at: string;
}

/** Staffing basis for automation-release math.
 *  - `modeled` — the engine's sized grid (default).
 *  - `modeled_adjusted` — the sized grid with manual overrides applied.
 *  - `actual` — the Actual staff plan attached to the scenario. */
export type AutomationBasis = 'modeled' | 'modeled_adjusted' | 'actual';

// ---------------------------------------------------------------------------
// Current Staff Plan — separate per-client artifact for comparing the
// modeled plan against the team a client has today. Stored independently of
// scenarios so a single plan can back many scenarios, but scenarios still
// embed a flattened `currentStaffByLevel` so the engine doesn't need to
// load the plan separately.
// ---------------------------------------------------------------------------

export interface StaffPlanCell {
  disciplineId: string;
  /** Channel id from `model.channels`, or 'all' for discipline-wide. */
  channelId: string;
  levelId: LevelId;
  /** Total FTE today. May be fractional. */
  fte: number;
  /** Onshore FTE. Legacy cells without this value are treated as 100% onshore. */
  onFte?: number;
  /** Hub FTE, combining nearshore and offshore roles. */
  hubFte?: number;
}

/** Suggested labels for roles outside the model's disciplines; free text is also allowed. */
export const STAFF_PLAN_OTHER_ROLE_OPTIONS = ['Influencer', 'SEO', 'Finance Ops'] as const;

/**
 * Headcount in a role the model has no discipline for (e.g. Influencer, SEO).
 * Shown with the plan and counted in its headline totals, but never fed into
 * calculations: nothing in the engine reads `StaffPlan.otherRoles`.
 */
export interface StaffPlanOtherRole {
  /** Display label, e.g. 'Influencer' or free text. */
  role: string;
  levelId: LevelId;
  fte: number;
  onFte?: number;
  hubFte?: number;
}

/** Location of the Hub roles captured by a staff plan. */
export interface StaffPlanHubLocation {
  hubId?: string;
  label: string;
  shore: 'nearshore' | 'offshore';
}

export interface StaffPlan {
  id: string;
  clientId: string;
  name: string;
  notes?: string;
  /** Model the plan was authored against (used to render the right channels/levels). */
  modelId: string;
  createdAt: string;
  updatedAt: string;
  cells: StaffPlanCell[];
  /** Roles outside the model's disciplines. Display-only; excluded from calculations. */
  otherRoles?: StaffPlanOtherRole[];
  /** Plan-wide default hub. Applied to any (discipline × channel) row that
   *  doesn't specify its own hub in `rowHubs`. */
  hubLocation?: StaffPlanHubLocation;
  /** Per-row hub overrides, keyed by `${disciplineId}::${channelId}`. When a
   *  row has an entry here it wins over `hubLocation` for that row's Hub FTE
   *  (used when costing the plan). Rows without an entry fall back to
   *  `hubLocation`, then to offshore. */
  rowHubs?: Record<string, StaffPlanHubLocation>;
  /** How the plan's numbers originated. Absent means 'actual': every plan
   *  saved before this field existed was hand-entered.
   *
   *  This is load-bearing, not just a label. Only an 'actual' plan may be
   *  attached to a scenario as `currentStaffPlanId`, because hub/OGS cost
   *  savings are measured against the team a client really has. Letting a
   *  modeled plan fill that slot would quietly compare the model to itself. */
  kind?: StaffPlanKind;
  /** Where a generated plan came from. Set for any kind other than 'actual'. */
  source?: StaffPlanSource;
  /**
   * How an *actual* plan's numbers were entered into the app.
   * - `manual` — typed/edited in the staff plan grid
   * - `uploaded` — produced via the staff-plan upload / parse wizard
   * Absent on legacy plans is treated as `manual`. Not used for modeled/final
   * plans (those use `kind` + `source` provenance instead).
   */
  entrySource?: StaffPlanEntrySource;
  /**
   * Original uploaded staff-plan workbook retained with this record.
   * Binary lives in object storage (or a filesystem sidecar); this is metadata only.
   */
  sourceFile?: StaffPlanSourceFile;
  createdBy?: ContentOwner;
  updatedBy?: ContentOwner;
}

/** How an actual staff plan was entered (upload wizard vs hand grid). */
export type StaffPlanEntrySource = 'manual' | 'uploaded';

/** Metadata for an Excel (or other) file attached to a staff plan. */
export interface StaffPlanSourceFile {
  fileName: string;
  contentType: string;
  sizeBytes: number;
  /** Object-store key or filesystem-relative path used by the API to fetch bytes. */
  storageKey: string;
  uploadedAt: string;
}

/**
 * How a staff plan's numbers came to be.
 *
 * - `actual` — hand-entered: the team the client really has.
 * - `modeled` — the engine's sized grid, optionally with manual adjustments.
 * - `final` — post-automation headcount at a chosen horizon: the end of the
 *   chain, since releasing automation again from an already-reduced roster
 *   would double-count.
 *
 * Only `actual` may back a scenario's cost-savings comparison, so a new kind
 * is safer than a flag on `modeled`: `isActualStaffPlan` whitelists `actual`,
 * which means every guard already refuses the new kind by default.
 */
export type StaffPlanKind = 'actual' | 'modeled' | 'final';

/** Point in a deployment plan a Final plan was taken at. */
export type StaffPlanHorizon = 'y1' | 'y2' | 'y3' | 'full';

/** Provenance for a staff plan generated from a scenario's output. */
export interface StaffPlanSource {
  scenarioId: string;
  scenarioName: string;
  modelId: string;
  generatedAt: string;
  /** How many cells the user hand-adjusted before publishing. Zero means the
   *  plan is the untouched modeled output. */
  adjustedCellCount: number;
  /** Final only — when in the deployment the headcount was read. */
  horizon?: StaffPlanHorizon;
  /** Final only — the staffing basis automation was applied to. */
  basis?: AutomationBasis;
  /** Final only — how many cells automation released time from. */
  automationCellCount?: number;
  /** Final only — total FTE released at the saved horizon. */
  releasedFte?: number;
  /** Final only — the scenario inputs that produced these numbers, frozen at
   *  save time. */
  snapshot?: StaffPlanScenarioSnapshot;
}

/**
 * The scenario inputs that produced a generated plan, frozen at save time.
 *
 * A scenario keeps moving after a plan is published — the budget changes, the
 * model is re-tuned, automations are re-timed — so reading the live scenario
 * would describe a plan with inputs that did not produce it. Freezing costs a
 * few KB in the plan's JSON payload and buys an artifact that explains itself
 * however long afterwards it is opened.
 */
export interface StaffPlanScenarioSnapshot {
  capturedAt: string;
  modelName?: string;
  modelVersion?: number;
  /** The intake as it stood: budget, markets, selections, OGS, leveling,
   *  automation timings and any hand adjustments. */
  profile: ClientProfile;
  automations?: StaffPlanSnapshotAutomation[];
  /** Headcount before and after the release, so the panel need not recompute
   *  from a model that may since have changed. */
  beforeFte?: number;
  afterFte?: number;
}

/** One automation's contribution to a Final plan, as resolved at save time. */
export interface StaffPlanSnapshotAutomation {
  id: string;
  name: string;
  startMonth: number;
  rampMonths: number;
  /** Fraction of full impact realised at the plan's horizon (0..1). */
  adoption: number;
  releasedFte: number;
}

// ---------------------------------------------------------------------------
// Spend Set — independently saved channel spend configuration, stored per
// client so a single spend set can back many scenarios. Scenarios can load
// a spend set to populate their channel spend profile fields, but remain
// self-contained (the spend data is copied onto the scenario profile, not
// referenced at calculation time).
// ---------------------------------------------------------------------------

export interface SpendSet {
  id: string;
  clientId: string;
  name: string;
  notes?: string;
  /** Model the spend set was authored against (determines the channel list). */
  modelId: string;
  totalBudget: number;
  /** Per-channel fractional shares (0–1), keyed by channel id. */
  channelAllocation: Record<string, number>;
  channelMixPresetId?: string;
  activeChannels?: Record<string, boolean>;
  adopsSupport?: Record<string, boolean>;
  createdAt: string;
  updatedAt: string;
  createdBy?: ContentOwner;
  updatedBy?: ContentOwner;
}

// ---------------------------------------------------------------------------
// Automation Solution Set — the automations a client can deploy (scope and
// time savings) plus their deployment timing. Stored per client and sent in by
// an external application. Scenarios copy the automations and deployments onto
// the profile when a set is applied; they do not live-reference it at calc.
// ---------------------------------------------------------------------------

/** Where a set came from when it was pushed in by another application. */
export interface AutomationSolutionSource {
  /** Sending application, e.g. "automation-catalog". */
  system: string;
  /** The sender's id for the set; re-sending the same id updates the set. */
  externalId?: string;
  receivedAt?: string;
}

export interface AutomationSolution {
  id: string;
  clientId: string;
  name: string;
  notes?: string;
  /** Model whose discipline / channel / sub-phase ids the automations use. */
  modelId?: string;
  automations: AutomationImpactRow[];
  /** Same shape as ClientProfile.automationDeployments — sparse overrides. */
  deployments: Record<string, AutomationDeploymentSetting>;
  source?: AutomationSolutionSource;
  /** True for the built-in dummy set seeded until the external feed is live. */
  sample?: boolean;
  /** Optional preferred staffing basis when this solution is applied. */
  automationBasis?: AutomationBasis;
  createdAt: string;
  updatedAt: string;
  createdBy?: ContentOwner;
  updatedBy?: ContentOwner;
}

export interface Client {
  id: string;
  name: string;
  /** @deprecated New clients are model-agnostic; scenarios and staff plans carry modelId. */
  modelId?: string;
  notes?: string;
  region?: string;
  /** Client industry from the shared `clients.industry` enum. */
  industry?: string;
  /** @deprecated Use `industry` on client rows; scenario profiles still use `vertical`. */
  vertical?: string;
  createdAt: string;
  updatedAt: string;
  /** Latest scenario id (for quick-open in the UI). */
  latestScenarioId?: string;
  /** When true, the client workspace is visible in GIA Calculator. Defaults to false in Postgres. */
  market?: ClientMarket;
}

export interface ClientMarket {
  /** `Global` or ISO country code (uppercase). */
  code?: string;
  label?: string;
  /** @deprecated Derived from `code`; kept for legacy UI payloads. */
  kind?: 'global' | 'region' | 'country';
}

export interface ManagedClient {
  id: string;
  name: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Which scenario editor the record opens in. Missing → advanced (legacy). */
export type ScenarioWorkspace = 'basic' | 'advanced';

export interface Scenario {
  id: string;
  clientId: string;
  name: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  modelId: string;
  /**
   * Editor mode. `advanced` is the full profile + modeled sizing workspace.
   * `basic` sizes impact from an attached staff plan + OGS proportion + automation.
   * Absent on legacy scenarios is treated as `advanced`.
   */
  workspace?: ScenarioWorkspace;
  profile: ClientProfile;
  /** Cached result snapshot — rehydrated lazily by the engine. */
  result?: CalculationResult;
  createdBy?: ContentOwner;
  updatedBy?: ContentOwner;
}

export interface ContentOwner {
  userId: string;
  email: string;
  displayName: string;
}

// ---------------------------------------------------------------------------
// Calculation result
// ---------------------------------------------------------------------------

export interface Stage2Row {
  disciplineId: string;
  disciplineLabel: string;
  driver: JuniorDriver;
  l1Raw: number;
  l1: number;
  drivingBudget: number;
  activationFraction: number;
  activationApplied: boolean;
  channelBreakdown: Stage2ChannelBreakdown[];
}

export interface Stage2ChannelBreakdown {
  channelId: string;
  channelLabel: string;
  category: ChannelCategory;
  share: number;
  dollars: number;
  l1: number;
}

export interface Stage3Row {
  disciplineId: string;
  disciplineLabel: string;
  pyramid: PyramidByLevel;
}

export type PyramidByLevel = Partial<Record<LevelId, number>> & { total: number };

export interface Stage4Cell {
  disciplineId: string;
  disciplineLabel: string;
  levelId: LevelId;
  levelLabel: string;
  baseFtePyramid: number;
  targetedMult: number;
  adjustedFte: number;
}

export interface ByDisciplineLevelCell {
  disciplineId: string;
  disciplineLabel: string;
  levelId: LevelId;
  levelLabel: string;
  baseFte: number;
  adjustedFte: number;
  targetedMult: number;
  sAutoY1: number;
  ogsY1: number;
  onshoreY1: number;
  sAutoY3: number;
  ogsY3: number;
  onshoreY3: number;
  currentFte: number;
  fteOnshoreY1: number;
  fteOgsY1: number;
  y1Total: number;
  y1Delta: number;
  y1Dollar: number;
  fteOnshoreY3: number;
  fteOgsY3: number;
  y3Total: number;
  y3Delta: number;
  y3Dollar: number;
  deltaAutoFte: number;
  onshoreRate: number;
  ogsDeltaRate: number;
}

export interface ByDisciplineRow {
  disciplineId: string;
  disciplineLabel: string;
  baseFte: number;
  sAutoFteY1: number;
  ogsFteY1: number;
  onshoreFteY1: number;
  sAutoFteY3: number;
  ogsFteY3: number;
  onshoreFteY3: number;
  hReq: number;
  sAuto: number;
  hOgsTarget: number;
  hOnshoreNet: number;
  fteReleased: number;
  currentOnshore: number;
  pctOfTotal: number;
}

export interface DisciplineSummaryRow {
  disciplineId: string;
  disciplineLabel: string;
  currentFte: number;
  y1OnshoreFte: number;
  y1OgsFte: number;
  y1Total: number;
  y1Delta: number;
  y1Dollar: number;
  y3OnshoreFte: number;
  y3OgsFte: number;
  y3Total: number;
  y3Delta: number;
  y3Dollar: number;
  deltaAutoFte: number;
  activationFraction: number;
  activationApplied: boolean;
}

export interface ByChannelRow {
  channelId: string;
  channelLabel: string;
  category: ChannelCategory;
  share: number;
  dollars: number;
  l1Allocated: number;
  /** Total FTE staffed against this channel across disciplines (v3). */
  totalFte?: number;
  /** Total $ staffed against this channel (v3). */
  totalCost?: number;
  disciplines: { disciplineId: string; disciplineLabel: string; l1: number }[];
}

export interface ByShoreRow {
  shore: Shore;
  label: string;
  fte: number;
  cost: number;
}

export interface DisciplineChannelCell {
  disciplineId: string;
  disciplineLabel: string;
  channelId: string;
  channelLabel: string;
  levelId: LevelId;
  levelLabel: string;
  baseFte: number;
  adjustedFte: number;
  onshoreFte: number;
  nearshoreFte: number;
  offshoreFte: number;
  onshoreCost: number;
  nearshoreCost: number;
  offshoreCost: number;
  totalCost: number;
  sAutoFte: number;
  postAutomationFte: number;
}

export interface CalculationResult {
  summary: CalculationSummary;
  byDiscipline: ByDisciplineRow[];
  byDisciplineLevel: ByDisciplineLevelCell[];
  disciplineSummary: DisciplineSummaryRow[];
  hoursDistribution: { automation: number; ogs: number; onshore: number };
  byChannel: ByChannelRow[];
  byShore: ByShoreRow[];
  /** Discipline × channel × level cells — the engine's output grain. */
  byDisciplineChannelLevel?: DisciplineChannelCell[];
  stages: {
    stage2: Stage2Row[];
    stage3: Stage3Row[];
    stage4: Stage4Cell[];
  };
}

export interface CalculationSummary {
  totalHReq: number;
  totalSAuto: number;
  totalHOgs: number;
  totalHOnshore: number;
  totalCurrentOnshore: number;
  totalBaseFte: number;
  totalSAutoFteY1: number;
  totalOgsFteY1: number;
  totalOnshoreFteY1: number;
  totalSAutoFteY3: number;
  totalOgsFteY3: number;
  totalOnshoreFteY3: number;
  totalCurrentFte: number;
  fteOnshoreReleasedY1: number;
  fteOgsY1: number;
  dollarOppY1: number;
  dollarOppY3: number;
  mAuto: number;
  mAutoY2: number;
  mAutoY3: number;
  mOmni: number;
  mEnterprisePlatform: number;
  structuralMult: number;
  totalBudget: number;
  engineMode: 'bottom-up' | 'direct-tables';
  /** Currency every monetary field in this result is expressed in. */
  currency?: CurrencyCode;
  /** v3 — total cost by shore. */
  totalCostOnshore?: number;
  totalCostNearshore?: number;
  totalCostOffshore?: number;
  totalCost?: number;
  totalFteOnshore?: number;
  totalFteNearshore?: number;
  totalFteOffshore?: number;
}
